/**
 * Decrypts the payloads the `/secrets/infisical` endpoints return.
 *
 * The backend encrypts with `SECRETS_ENCRYPTION_KEY` (AES-256, 32-byte hex); the same key
 * reaches the browser as `REACT_APP_SECRETS_ENCRYPTION_KEY`.
 *
 * The shape the live backend sends, confirmed against `GET /secrets/infisical`:
 *
 *   { payload: "<iv>.<tag>.<ciphertext>",   // base64 parts, dot-separated
 *     algorithm: "aes-256-gcm",
 *     encoding: "iv.tag.ciphertext" }       // literal part order
 *
 * `encoding` is parsed rather than trusted by position, so a backend that reorders to
 * `iv.ciphertext.tag` keeps working. Other shapes are still accepted, since the rest of the
 * API layer parses leniently too:
 *
 *   "<iv>.<tag>.<ciphertext>"        — bare envelope string, `.` or `:` separated
 *   "<iv>:<ciphertext>"              — GCM with the tag appended, or CBC
 *   "<blob>"                         — iv ‖ ciphertext ‖ tag in one string (12-byte iv assumed)
 *   { iv, tag, ciphertext }          — object form; `data`, `content`, `value`, `encryptedData`
 *                                      and `authTag` / `auth_tag` are accepted as aliases
 *
 * Each part may be hex or base64. `algorithm` selects the cipher when present; otherwise
 * AES-256-GCM is tried first, then AES-256-CBC. The plaintext is JSON-parsed when it is JSON,
 * returned as a string otherwise.
 *
 * NOTE: because CRA inlines `REACT_APP_*` into the bundle, this key is visible to anyone who
 * loads the panel — it protects the response in transit/at rest on the backend, not from the
 * panel's own users.
 */

const KEY_ENV = "REACT_APP_SECRETS_ENCRYPTION_KEY";
const HEX_KEY = /^[0-9a-fA-F]{64}$/;
const HEX_ONLY = /^[0-9a-fA-F]+$/;
const GCM_TAG_BYTES = 16;
const GCM_IV_BYTES = 12;
const CBC_IV_BYTES = 16;

const CIPHERTEXT_KEYS = [
  "ciphertext",
  "cipher_text",
  "encryptedData",
  "encrypted_data",
  "encrypted",
  "data",
  "content",
  "value",
];
const IV_KEYS = ["iv", "nonce", "initializationVector", "initialization_vector"];
const TAG_KEYS = ["tag", "authTag", "auth_tag", "authenticationTag", "mac"];

/** Part names `encoding` may use, mapped to the field they fill. */
const PART_ALIASES = {
  iv: "iv", nonce: "iv",
  tag: "tag", authtag: "tag", auth_tag: "tag", mac: "tag",
  ciphertext: "ciphertext", cipher_text: "ciphertext", data: "ciphertext",
  encrypted: "ciphertext", encrypteddata: "ciphertext", content: "ciphertext",
};
/**
 * Fallback order when the backend sends no `encoding`, keyed by part count. Two parts means
 * the tag is appended to the ciphertext (or the cipher is CBC), so the second part is the
 * ciphertext — never the tag.
 */
const DEFAULT_PART_ORDER = { 2: ["iv", "ciphertext"], 3: ["iv", "tag", "ciphertext"] };
/** One base64/base64url/hex part — strict enough that prose never looks like an envelope. */
const PART_CHARS = /^[A-Za-z0-9+/_-]+={0,2}$/;

/** The configured key, trimmed. Empty string when unset. */
export function getEncryptionKeyHex() {
  const raw = process.env[KEY_ENV];
  return typeof raw === "string" ? raw.trim() : "";
}

/** True when a usable 32-byte hex key is configured. */
export function hasEncryptionKey() {
  return HEX_KEY.test(getEncryptionKeyHex());
}

// ─── encoding helpers ─────────────────────────────────────────────────────────

function fromHex(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function fromBase64(value) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Hex when the string looks like hex (even length, hex alphabet), base64 otherwise. */
function toBytes(value, label) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) throw new Error(`Encrypted payload is missing its ${label}.`);
  try {
    if (trimmed.length % 2 === 0 && HEX_ONLY.test(trimmed)) return fromHex(trimmed);
    return fromBase64(trimmed);
  } catch {
    throw new Error(`Encrypted payload has an unreadable ${label}.`);
  }
}

function concat(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

function pick(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

// ─── envelope parsing ─────────────────────────────────────────────────────────

/** Splits an envelope string on `.` or `:`, whichever it actually uses. */
function splitParts(envelope) {
  const dot = envelope.split(".").filter(Boolean);
  if (dot.length > 1) return dot;
  return envelope.split(":").filter(Boolean);
}

/** Turns `encoding` ("iv.tag.ciphertext") into `["iv", "tag", "ciphertext"]`. */
function parsePartOrder(encoding, count) {
  const named = String(encoding ?? "")
    .split(/[.:,\s]+/)
    .map((part) => PART_ALIASES[part.trim().toLowerCase()])
    .filter(Boolean);

  if (named.length === count && new Set(named).size === count) return named;
  return DEFAULT_PART_ORDER[count] ?? [];
}

/**
 * Normalises any supported envelope into `{ iv, ciphertext, tag, algorithm }`.
 * `tag` is null when the backend appended it to the ciphertext (or used CBC).
 */
function parseEnvelope(payload, encoding, algorithm) {
  if (typeof payload === "string") {
    const parts = splitParts(payload.trim());

    if (parts.length >= 2) {
      const order = parsePartOrder(encoding, parts.length);
      const byName = {};
      order.forEach((name, i) => { byName[name] = parts[i]; });

      if (!byName.iv || !byName.ciphertext) {
        throw new Error("Encrypted payload is missing its iv or ciphertext.");
      }
      return {
        iv: toBytes(byName.iv, "iv"),
        tag: byName.tag ? toBytes(byName.tag, "auth tag") : null,
        ciphertext: toBytes(byName.ciphertext, "ciphertext"),
        algorithm,
      };
    }

    // Single blob: iv is prefixed, tag (if any) is suffixed.
    const blob = toBytes(parts[0] ?? "", "ciphertext");
    if (blob.length <= GCM_IV_BYTES + GCM_TAG_BYTES) {
      throw new Error("Encrypted payload is too short to contain an iv and ciphertext.");
    }
    return {
      iv: blob.slice(0, GCM_IV_BYTES), tag: null, ciphertext: blob.slice(GCM_IV_BYTES), algorithm,
    };
  }

  if (payload && typeof payload === "object") {
    // `{ payload, algorithm, encoding }` — the live backend's wrapper.
    const wrapped = typeof payload.payload === "string" ? payload.payload.trim() : "";
    if (wrapped) {
      return parseEnvelope(wrapped, payload.encoding ?? encoding, payload.algorithm ?? algorithm);
    }

    const iv = pick(payload, IV_KEYS);
    const ciphertext = pick(payload, CIPHERTEXT_KEYS);
    if (!iv || !ciphertext) throw new Error("Encrypted payload is missing its iv or ciphertext.");
    const tag = pick(payload, TAG_KEYS);
    return {
      iv: toBytes(iv, "iv"),
      tag: tag ? toBytes(tag, "auth tag") : null,
      ciphertext: toBytes(ciphertext, "ciphertext"),
      algorithm: payload.algorithm ?? algorithm,
    };
  }

  throw new Error("Encrypted payload is not a string or object.");
}

/**
 * Cheap shape check — does this value look like something we should try to decrypt?
 *
 * Deliberately strict for strings: every part must be base64/hex-shaped *and* the first part
 * must decode to a plausible iv length, so ordinary prose with dots ("Handshake succeeded.
 * 5 secrets visible.") is never mistaken for an envelope.
 */
export function looksEncrypted(value) {
  if (typeof value === "string") {
    const parts = splitParts(value.trim());
    if (parts.length < 2 || parts.length > 3) return false;
    if (!parts.every((part) => PART_CHARS.test(part.trim()))) return false;
    try {
      const ivLength = toBytes(parts[0], "iv").length;
      return ivLength === GCM_IV_BYTES || ivLength === CBC_IV_BYTES;
    } catch {
      return false;
    }
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    if (typeof value.payload === "string") return looksEncrypted(value.payload);
    return Boolean(pick(value, IV_KEYS) && pick(value, CIPHERTEXT_KEYS));
  }
  return false;
}

// ─── decryption ───────────────────────────────────────────────────────────────

function subtle() {
  // `window` rather than `globalThis` — CRA's eslint env predates the latter.
  const api = window.crypto?.subtle;
  if (!api) {
    throw new Error(
      "WebCrypto is unavailable — serve the admin panel over HTTPS (or localhost) to decrypt secrets."
    );
  }
  return api;
}

function keyBytes() {
  const hex = getEncryptionKeyHex();
  if (!hex) throw new Error(`${KEY_ENV} is not set — cannot decrypt the response.`);
  if (!HEX_KEY.test(hex)) throw new Error(`${KEY_ENV} must be 64 hex characters (a 32-byte AES-256 key).`);
  return fromHex(hex);
}

const DECRYPT_FAILED =
  "Failed to decrypt the response — wrong encryption key or corrupt payload.";

async function decryptBytes({ iv, ciphertext, tag, algorithm }) {
  const raw = keyBytes();
  const api = subtle();
  const body = tag ? concat(ciphertext, tag) : ciphertext;
  const named = String(algorithm ?? "").toLowerCase();

  // `algorithm` from the envelope wins; with no hint, try GCM then CBC.
  if (!named.includes("cbc")) {
    try {
      const key = await api.importKey("raw", raw, { name: "AES-GCM" }, false, ["decrypt"]);
      const plain = await api.decrypt(
        { name: "AES-GCM", iv, tagLength: GCM_TAG_BYTES * 8 }, key, body
      );
      return new TextDecoder().decode(plain);
    } catch {
      // A stated GCM algorithm that fails to authenticate is a key/payload problem, not a
      // reason to try another cipher.
      if (named.includes("gcm") || tag || iv.length !== CBC_IV_BYTES) {
        throw new Error(DECRYPT_FAILED);
      }
    }
  }

  try {
    const key = await api.importKey("raw", raw, { name: "AES-CBC" }, false, ["decrypt"]);
    const plain = await api.decrypt({ name: "AES-CBC", iv }, key, ciphertext);
    return new TextDecoder().decode(plain);
  } catch {
    throw new Error(DECRYPT_FAILED);
  }
}

/**
 * Decrypts an envelope and returns the plaintext, JSON-parsed when it is JSON.
 * Throws a user-facing `Error` when the key is missing or the payload does not authenticate.
 */
export async function decryptSecretPayload(payload) {
  const plaintext = await decryptBytes(parseEnvelope(payload));
  try {
    return JSON.parse(plaintext);
  } catch {
    return plaintext;
  }
}

/**
 * Transport-level helper: decrypt `value` if it is an envelope, otherwise return it as-is.
 * Lets a response body switch between plaintext JSON and an encrypted envelope without the
 * calling screen caring which one arrived.
 */
export async function resolveMaybeEncrypted(value) {
  return looksEncrypted(value) ? decryptSecretPayload(value) : value;
}
